import { Action } from '../types/types';
import * as A from '../actions/themes';
import { handle } from 'redux-pack';
import { Theme, ThemeMaps } from '../types/themes';


export enum THEME_FILTER {
	SUGGESTED = 1,
	CUSTOM = 2
}

export interface ThemesState {
	defaultThemes: Theme[],
	customThemes: Theme[],
	viewedTheme: Theme | null,
	// theme editing is false when "editing"/changing a default
	// theme before it is named (see SET_EDITING_THEME)
	themeEditing: boolean, 
	attachedThemes: Record<string, number>,
	themeFilter: THEME_FILTER,
	themeMaps: ThemeMaps | null
}

export const intialThemesState: ThemesState = {
	defaultThemes: [],
	customThemes: [],
	viewedTheme: null,
	themeEditing: false,
	attachedThemes: {},
	themeFilter: THEME_FILTER.SUGGESTED,
	themeMaps: null
}

const actions: Map<string, (
	statestore: ThemesState,
	action: Action
) => ThemesState> = new Map([
	[A.LOAD_THEMES, (s: ThemesState, action: Action) => handle(s, action, {
		start: (s) => s,
		finish: (s) => s,
		failure: (s) => s,
		success: (s) => ({...s, customThemes: action?.payload ?? []})
	})],
	[A.LOAD_DEFAULT_THEMES, (s: ThemesState, action: Action) => handle(s, action, {
		start: (s) => s,
		finish: (s) => s,
		failure: (s) => s,
		success: (s) => ({...s, defaultThemes: action?.payload ?? []})
	})],
	[A.DELETE_THEME, (s: ThemesState, action: Action) => {
		return {
			...s,
			customThemes: s.customThemes.filter((t) => t.id !== action.payload),
			attachedThemes: Object.fromEntries(
				Object.entries(s.attachedThemes).filter(([_, themeId]) => themeId !== action.payload)
			)
		}
	}],
	[A.UPSERT_THEME, (s: ThemesState, action: Action) => handle(s, action, {
		start: (s) => s,
		finish: (s) => s,
		failure: (s) => s,
		success: (s) => {
			if (!action?.payload?.id) return s;
			let didExist = false;
			const customThemes = s.customThemes.reduce((acc: Theme[], curr: Theme) => {
				if (curr.id === action.payload.id) {
					didExist = true;
					acc.push(action.payload);
				} else {
					acc.push(curr);
				}	
				return acc;
			}, []);
			
			if (!didExist) {
				customThemes.push(action.payload);
			}
			return {
				...s,
				customThemes,
				viewedTheme: null,
				themeEditing: false,
				themeFilter: THEME_FILTER.CUSTOM
			}
		}
	})],
	[A.SET_EDITING_THEME, (s: ThemesState, action: Action) => {
		const matchTheme = (t: Theme) => (t.id === action.payload.themeId);
		let nTheme = s.defaultThemes.find(matchTheme) ?? null;
		if (nTheme) {
			return {...s, viewedTheme: nTheme, themeEditing: false}
		} else {
			nTheme = s.customThemes.find(matchTheme) ?? null;
		}
		return {
			...s,
			viewedTheme: nTheme,
			themeEditing: true
		}
	}],
	[A.SET_CREATING_THEME, (s: ThemesState, action: Action) => {
		const allThemes = [...s.defaultThemes, ...s.customThemes];
		let theme;
		if (action.payload.currItemUniqueId) {
			const themeId = s.attachedThemes[action.payload.currItemUniqueId];
			theme = allThemes.find((t) => t.id === themeId);
		}
		if (!theme) {
			theme = allThemes.find((t) => t.stylepack === action.payload.stylepack)
		}
		return {
			...s,
			viewedTheme: theme ? {...theme, id: 0, name: 'My new style'} : null,
			themeEditing: false
		}
	}],
	[A.SET_THEME_FILTER, (s: ThemesState, action: Action) => ({
		...s,
		themeFilter: action.payload
	})],
	[A.ATTACH_THEME, (s: ThemesState, action: Action) => ({
		...s,
		attachedThemes: {
			...s.attachedThemes,
			[action.payload.uniqueId]: action.payload.themeId
		}
	})],
	[A.LOAD_THEME_MAPS, (s: ThemesState, action: Action) => ({
		...s,
		themeMaps: action.payload
	})],
	[A.SET_THEME_FONT, (s: ThemesState, action: Action) => handle(s, action, {
		start: (s) => s,
		finish: (s) => s,
		failure: (s) => s,
		success: (s) => s
	})],
]);

export function ThemesReducer(s: ThemesState = intialThemesState, action: Action) {
	return actions.get(action.type)?.(s, action) || s;
}